import type {
  GetProjectDetailsResponse,
  UpdateProjectDetails,
} from "@nlc/shared-types";

import { FormProvider, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue";
import { isTeamLeaderUser } from "@/features/requests/utils/roleUtils";
import { useUser } from "@/hooks/useUser";

import { AccountDetailsSection } from "./components/AccountDetailsSection";
import { ProductSettingsSection } from "./components/ProductSettingsSection";
import { SlotSettingsSection } from "./components/SlotSettingsSection";
import { TeamDetailsSection } from "./components/TeamDetailsSection";
import { TogglesSection } from "./components/TogglesSection";
import { useUpdateProjectDesignerPreferences } from "./hooks/useUpdateProjectDesignerPreferences";
import { useUpdateProjectDetails } from "./hooks/useUpdateProjectDetails";
import { ProjectDetailsProvider } from "./ProjectDetailsContext";

interface ProjectDetailsPresenterProps {
  projectDetails: GetProjectDetailsResponse;
  projectId: number;
}

const toIsoDate = (value: Date | null | string | undefined): string =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

const mapDetailsToFormValues = (projectDetails: GetProjectDetailsResponse) => ({
  accountName: projectDetails.accountName,
  allowedCoupons: projectDetails.allowedCoupons,
  assignedCustomerSuccessId: projectDetails.assignedCustomerSuccess?.id ?? null,
  assignedTeamIds: projectDetails.assignedTeams.map((t) => t.id),
  cancelledDate: toIsoDate(projectDetails.billing.cancelledDate),
  closeInactiveRequestsAfterDays: projectDetails.closeInactiveRequestsAfterDays,
  nonPreferredDesignerIds: projectDetails.nonPreferredDesigners.map(
    (d) => d.id,
  ),
  preferredDesignerIds: projectDetails.preferredDesigners.map((d) => d.id),
  productId: projectDetails.productId,
  renewalDate: toIsoDate(projectDetails.billing.renewalDate),
  slots: projectDetails.slots,
  subscriptionId: projectDetails.billing.subscriptionId,
  toggles: projectDetails.toggles,
  website: projectDetails.website,
});

type ProjectDetailsFormValues = ReturnType<typeof mapDetailsToFormValues>;

const handleSaveError = () => {
  toast.add({ title: "Failed to update details", type: "error" });
};

export function ProjectDetailsForm({
  projectDetails,
  projectId,
}: ProjectDetailsPresenterProps) {
  const { data: userData } = useUser();
  const { isPending, mutate: updateProject } =
    useUpdateProjectDetails(projectId);
  const { isPending: isDesignerPrefsPending, mutate: updateDesignerPrefs } =
    useUpdateProjectDesignerPreferences(projectId);

  const isTeamLeader = isTeamLeaderUser(userData?.user?.roleName);
  const isReadOnly = isTeamLeader;

  const form = useForm<ProjectDetailsFormValues>({
    defaultValues: mapDetailsToFormValues(projectDetails),
  });

  const handleSaveSuccess = (resetValues: ProjectDetailsFormValues) => {
    toast.add({
      timeout: 4000,
      title: "Project details updated",
      type: "success",
    });
    form.reset(resetValues);
  };

  const onSubmit = (data: ProjectDetailsFormValues) => {
    if (!userData) return;

    if (!form.formState.isDirty) {
      toast.add({ title: "No changes to save", type: "warning" });

      return;
    }

    if (isTeamLeader) {
      updateDesignerPrefs(
        {
          nonPreferredDesignerIds: data.nonPreferredDesignerIds ?? [],
          preferredDesignerIds: data.preferredDesignerIds ?? [],
        },
        {
          onError: handleSaveError,
          onSuccess: () => handleSaveSuccess(form.getValues()),
        },
      );

      return;
    }

    const { dirtyFields } = form.formState;
    const partialData: UpdateProjectDetails = {};

    for (const key of Object.keys(dirtyFields) as Array<
      keyof ProjectDetailsFormValues
    >) {
      if (key === "slots") {
        if (dirtyFields.slots) partialData.slots = data.slots;
      } else if (key === "toggles") {
        if (dirtyFields.toggles) partialData.toggles = data.toggles;
      } else if (dirtyFields[key]) {
        (partialData as Record<string, unknown>)[key] = data[key];
      }
    }

    updateProject(partialData, {
      onError: handleSaveError,
      onSuccess: (updatedProject) =>
        handleSaveSuccess(mapDetailsToFormValues(updatedProject)),
    });
  };

  const isSaving = isPending || isDesignerPrefsPending;

  return (
    <FormProvider {...form}>
      <ProjectDetailsProvider
        // Team leaders have isReadOnly=true but can still edit designer prefs;
        // they submit via updateDesignerPrefs rather than updateProject.
        isDesignerPrefsReadOnly={false}
        isReadOnly={isReadOnly}
        projectId={projectId}
      >
        <form
          className="space-y-5 pb-20"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <AccountDetailsSection />
          <TeamDetailsSection />
          <ProductSettingsSection />
          <SlotSettingsSection />
          <TogglesSection />

          <div className="fixed bottom-0 left-0 right-0 bg-bg-primary border-t border-border-primary p-4 flex justify-end z-10">
            <Button
              isDisabled={isSaving || !form.formState.isDirty || !userData}
              type="submit"
              variant="primary"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </ProjectDetailsProvider>
    </FormProvider>
  );
}
